import React, { useEffect, useState, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, ArcElement,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { FaChartLine, FaMoneyBillWave, FaShoppingCart, FaClock, FaPiggyBank, FaUniversity } from 'react-icons/fa';
import { toast } from 'react-toastify';
import reportService from '../services/reportService';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

const fmt = (num) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0);

const QUICK_FILTERS = [
  { label: 'Bugün',   days: 0  },
  { label: '1 Hafta', days: 7  },
  { label: '1 Ay',    days: 30 },
  { label: '3 Ay',    days: 90 },
];

const toDateStr = (d) => d.toISOString().split('T')[0];

const getRange = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - days);
  return { start: toDateStr(start), end: toDateStr(end) };
};

const chartOptions = (title) => ({
  responsive: true,
  plugins: { legend: { position: 'top' }, title: { display: false } },
  scales: { y: { beginAtZero: true } },
});

const hBarOptions = (title) => ({
  indexAxis: 'y',
  responsive: true,
  plugins: { legend: { display: false } },
  scales: { x: { beginAtZero: true } },
});

const KpiCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="col-6 col-md-4 col-lg-2 mb-3">
    <div className={`card border-0 shadow-sm h-100`} style={{ borderLeft: `4px solid ${color}` }}>
      <div className="card-body p-3">
        <div className="d-flex align-items-center mb-1">
          <Icon style={{ color }} className="me-2 flex-shrink-0" />
          <small className="text-muted fw-semibold text-truncate">{label}</small>
        </div>
        <div className="fw-bold fs-6" style={{ color }}>{value}</div>
        {sub && <small className="text-muted">{sub}</small>}
      </div>
    </div>
  </div>
);

const ReportsPage = () => {
  const [summary, setSummary] = useState(null);
  const [monthlySales, setMonthlySales] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [topCustomers, setTopCustomers] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);

  const fetchAll = useCallback(async (sd, ed) => {
    setLoading(true);
    try {
      const [sum, mSales, fin, cust, prod, stk] = await Promise.all([
        reportService.getSummary(sd, ed),
        reportService.getMonthlySales(sd, ed),
        reportService.getFinancialSummary(sd, ed),
        reportService.getTopCustomers(sd, ed),
        reportService.getTopProducts(sd, ed),
        reportService.getStockReport(),
      ]);

      setSummary(sum);

      // Aylık satış cirosu
      setMonthlySales({
        labels: mSales.map(r => r.month),
        datasets: [{
          label: 'Satış Cirosu (₺)',
          data: mSales.map(r => parseFloat(r.total)),
          backgroundColor: 'rgba(192, 57, 43, 0.6)',
          borderColor: 'rgb(192, 57, 43)',
          borderWidth: 1,
        }],
      });

      // Gelir/Gider
      const allMonths = Array.from(new Set([...fin.income.map(i => i.month), ...fin.expense.map(e => e.month)])).sort();
      setFinancialData({
        labels: allMonths,
        datasets: [
          {
            label: 'Tahsilat / Gelir',
            data: allMonths.map(m => parseFloat(fin.income.find(i => i.month === m)?.total || 0)),
            borderColor: 'rgb(25, 135, 84)', backgroundColor: 'rgba(25, 135, 84, 0.4)', tension: 0.3, fill: true,
          },
          {
            label: 'Ödeme / Gider',
            data: allMonths.map(m => parseFloat(fin.expense.find(e => e.month === m)?.total || 0)),
            borderColor: 'rgb(220, 53, 69)', backgroundColor: 'rgba(220, 53, 69, 0.4)', tension: 0.3, fill: true,
          },
        ],
      });

      // En iyi müşteriler
      setTopCustomers({
        labels: cust.map(c => c.company_name),
        datasets: [{
          label: 'Ciro (₺)',
          data: cust.map(c => parseFloat(c.total_revenue)),
          backgroundColor: 'rgba(41, 128, 185, 0.65)',
        }],
      });

      // En çok satan ürünler
      setTopProducts({
        labels: prod.map(p => p.product_name || 'Bilinmeyen'),
        datasets: [{
          label: 'Satış Adedi',
          data: prod.map(p => parseFloat(p.total_quantity)),
          backgroundColor: 'rgba(243, 156, 18, 0.65)',
        }],
      });

      // Stok
      setStockData({
        labels: stk.map(s => s.category_name || 'Kategorisiz'),
        datasets: [{
          label: 'Stok Miktarı',
          data: stk.map(s => s.total_stock),
          backgroundColor: 'rgba(52, 152, 219, 0.6)',
        }],
      });
    } catch {
      toast.error('Rapor verileri alınamadı.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll('', ''); }, [fetchAll]);

  const applyQuickFilter = (days) => {
    setActiveFilter(days);
    if (days === null) {
      setStartDate(''); setEndDate('');
      fetchAll('', '');
    } else {
      const { start, end } = getRange(days);
      setStartDate(start); setEndDate(end);
      fetchAll(start, end);
    }
  };

  const handleManualFilter = (e) => {
    e.preventDefault();
    setActiveFilter(null);
    fetchAll(startDate, endDate);
  };

  return (
    <div className="container-fluid mt-4">

      {/* Başlık + Filtreler */}
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <h2 className="text-secondary mb-0">Raporlar</h2>

        <div className="d-flex flex-wrap gap-2 align-items-center">
          {/* Hızlı filtre butonları */}
          <div className="btn-group">
            {QUICK_FILTERS.map(f => (
              <button
                key={f.days}
                type="button"
                className={`btn btn-sm ${activeFilter === f.days ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => applyQuickFilter(f.days)}
              >
                {f.label}
              </button>
            ))}
            <button
              type="button"
              className={`btn btn-sm ${activeFilter === null && !startDate ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => applyQuickFilter(null)}
            >
              Tümü
            </button>
          </div>

          {/* Manuel tarih aralığı */}
          <form onSubmit={handleManualFilter} className="d-flex gap-1 align-items-center">
            <input type="date" className="form-control form-control-sm" value={startDate}
              onChange={e => { setStartDate(e.target.value); setActiveFilter(null); }} style={{ width: 140 }} />
            <span className="text-muted">—</span>
            <input type="date" className="form-control form-control-sm" value={endDate}
              onChange={e => { setEndDate(e.target.value); setActiveFilter(null); }} style={{ width: 140 }} />
            <button type="submit" className="btn btn-sm btn-primary">Filtrele</button>
          </form>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-2 text-muted">Veriler yükleniyor...</p>
        </div>
      ) : (
        <>
          {/* KPI Kartları */}
          {summary && (
            <div className="row mb-4">
              <KpiCard icon={FaChartLine}    label="Toplam Ciro"    value={`₺${fmt(summary.ciro)}`}         color="#c0392b" sub={`${summary.satis_sayisi} satış`} />
              <KpiCard icon={FaMoneyBillWave} label="Tahsil Edilen" value={`₺${fmt(summary.tahsilat)}`}      color="#27ae60" />
              <KpiCard icon={FaClock}         label="Bekleyen Alacak" value={`₺${fmt(summary.bekleyen)}`}   color="#e67e22" />
              <KpiCard icon={FaShoppingCart}  label="Yapılan Ödeme"  value={`₺${fmt(summary.odeme)}`}       color="#8e44ad" />
              <KpiCard icon={FaPiggyBank}     label="Kasa Bakiyesi"  value={`₺${fmt(summary.kasa_bakiye)}`} color="#2980b9" />
              <KpiCard icon={FaUniversity}    label="Banka Bakiyesi" value={`₺${fmt(summary.banka_bakiye)}`} color="#16a085" />
            </div>
          )}

          <div className="row">
            {/* Aylık Satış Cirosu */}
            <div className="col-md-6 mb-4">
              <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-semibold">Aylık Satış Cirosu</div>
                <div className="card-body">
                  {monthlySales?.labels?.length > 0
                    ? <Bar options={chartOptions()} data={monthlySales} />
                    : <p className="text-muted text-center pt-4">Bu dönemde satış verisi yok.</p>}
                </div>
              </div>
            </div>

            {/* Gelir / Gider */}
            <div className="col-md-6 mb-4">
              <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-semibold">Aylık Tahsilat / Ödeme</div>
                <div className="card-body">
                  {financialData?.labels?.length > 0
                    ? <Line options={chartOptions()} data={financialData} />
                    : <p className="text-muted text-center pt-4">Bu dönemde veri yok.</p>}
                </div>
              </div>
            </div>

            {/* En İyi Müşteriler */}
            <div className="col-md-6 mb-4">
              <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-semibold">En Yüksek Cirolu Müşteriler</div>
                <div className="card-body">
                  {topCustomers?.labels?.length > 0
                    ? <Bar options={hBarOptions()} data={topCustomers} />
                    : <p className="text-muted text-center pt-4">Bu dönemde müşteri verisi yok.</p>}
                </div>
              </div>
            </div>

            {/* En Çok Satan Ürünler */}
            <div className="col-md-6 mb-4">
              <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-semibold">En Çok Satan Ürünler</div>
                <div className="card-body">
                  {topProducts?.labels?.length > 0
                    ? <Bar options={hBarOptions()} data={topProducts} />
                    : <p className="text-muted text-center pt-4">Bu dönemde satış verisi yok.</p>}
                </div>
              </div>
            </div>

            {/* Kategori Bazlı Stok */}
            <div className="col-md-6 mb-4">
              <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-semibold">Kategori Bazlı Stok</div>
                <div className="card-body">
                  {stockData?.labels?.length > 0
                    ? <Bar options={chartOptions()} data={stockData} />
                    : <p className="text-muted text-center pt-4">Stok verisi yok.</p>}
                </div>
              </div>
            </div>

            {/* Ciro Tablosu - Müşteri detayı */}
            {topCustomers?.labels?.length > 0 && (
              <div className="col-md-6 mb-4">
                <div className="card shadow-sm h-100">
                  <div className="card-header bg-white fw-semibold">Müşteri Ciro Tablosu</div>
                  <div className="card-body p-0">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Müşteri</th>
                          <th className="text-end">Ciro</th>
                          <th className="text-center">Satış</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topCustomers.labels.map((name, i) => (
                          <tr key={i}>
                            <td><span className="badge bg-primary">{i + 1}</span></td>
                            <td>{name}</td>
                            <td className="text-end fw-semibold">₺{fmt(topCustomers.datasets[0].data[i])}</td>
                            <td className="text-center text-muted" style={{ fontSize: 12 }}>—</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ReportsPage;
